import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Shield, Users, QrCode, Activity, Search, ChevronLeft, ChevronRight,
  ShieldCheck, ShieldOff, Clock, AlertTriangle, Bug, CheckCircle2, Eye,
  Wand2, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-5 border border-[hsl(var(--border))]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon size={20} /></div>
        <div>
          <p className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">{label}</p>
        </div>
      </div>
    </Card>
  );
}

export default function AdminPanel() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('users');
  const [qrCodes, setQrCodes] = useState([]);
  const [qrTotal, setQrTotal] = useState(0);
  const [qrPage, setQrPage] = useState(1);
  const [qrTotalPages, setQrTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  // Error logs state
  const [errors, setErrors] = useState([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsTotalPages, setErrorsTotalPages] = useState(1);
  const [errorsCounts, setErrorsCounts] = useState({ open: 0, investigating: 0, resolved: 0 });
  const [errorsFilter, setErrorsFilter] = useState('');
  const [errorsSourceFilter, setErrorsSourceFilter] = useState('');
  const [selectedError, setSelectedError] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [selectedErrorIds, setSelectedErrorIds] = useState(new Set());

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers, params: { page: usersPage, search } });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
      setUsersTotalPages(res.data.total_pages);
    } catch {}
  }, [token, usersPage, search]);

  const fetchQrCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/qr-codes`, { headers, params: { page: qrPage, search } });
      setQrCodes(res.data.qr_codes);
      setQrTotal(res.data.total);
      setQrTotalPages(res.data.total_pages);
    } catch {}
  }, [token, qrPage, search]);

  const fetchErrors = useCallback(async () => {
    try {
      const params = { page: errorsPage };
      if (errorsFilter) params.status = errorsFilter;
      if (errorsSourceFilter) params.source = errorsSourceFilter;
      const res = await axios.get(`${API}/errors`, { headers, params });
      setErrors(res.data.errors);
      setErrorsTotal(res.data.total);
      setErrorsTotalPages(res.data.total_pages);
      setErrorsCounts(res.data.counts);
    } catch {}
  }, [token, errorsPage, errorsFilter, errorsSourceFilter]);

  const investigateError = async (errorId) => {
    try {
      await axios.patch(`${API}/errors/${errorId}/investigate`, {}, { headers });
      toast.success('Error marked as investigating');
      fetchErrors();
      if (selectedError?.error_id === errorId) {
        setSelectedError(prev => ({ ...prev, status: 'investigating' }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resolveError = async (errorId) => {
    if (!resolveNotes.trim()) {
      toast.error('Resolution notes are required');
      return;
    }
    try {
      await axios.patch(`${API}/errors/${errorId}/resolve`, { resolution_notes: resolveNotes }, { headers });
      toast.success('Error resolved and archived');
      setResolveNotes('');
      setSelectedError(null);
      fetchErrors();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openErrorDetail = async (errorId) => {
    try {
      const res = await axios.get(`${API}/errors/${errorId}`, { headers });
      setSelectedError(res.data);
    } catch (e) { toast.error('Could not load error details'); }
  };

  const copyFixPrompt = (err) => {
    const lines = [
      `Fix error ${err.error_id} from the error_logs database and mark it resolved when done.`,
      ``,
      `## Error Details`,
      `- **Error ID**: ${err.error_id}`,
      `- **Status**: ${err.status}`,
      `- **Source**: ${err.source}`,
      `- **Type**: ${err.error_type}`,
      `- **Message**: ${err.error_message}`,
      err.status_code ? `- **HTTP Status**: ${err.status_code}` : null,
      err.endpoint ? `- **Endpoint**: ${err.method || '?'} ${err.endpoint}` : null,
      err.location ? `- **Location**: ${err.location}` : null,
      err.cause ? `- **Cause**: ${err.cause}` : null,
      err.user_email ? `- **User**: ${err.user_email}` : null,
      err.page_url ? `- **Page**: ${err.page_url}` : null,
      err.browser_info ? `- **Browser**: ${err.browser_info}` : null,
      `- **When**: ${err.created_at}`,
    ].filter(Boolean);

    if (err.stack_trace) {
      lines.push(``, `## Stack Trace`, '```', err.stack_trace, '```');
    }
    if (err.request_body) {
      lines.push(``, `## Request Body`, '```json', JSON.stringify(err.request_body, null, 2), '```');
    }

    lines.push(
      ``,
      `## Instructions`,
      `1. Read this error from the error_logs collection (error_id: ${err.error_id})`,
      `2. Investigate the root cause using the stack trace and endpoint info above`,
      `3. Fix the code`,
      `4. Test the fix`,
      `5. Mark this error as resolved via PATCH /api/admin/errors/${err.error_id}/resolve with resolution notes describing what was fixed`,
    );

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      toast.success('Copied to clipboard — paste in Emergent chat to start the fix', { duration: 5000 });
    }).catch(() => {
      toast.error('Could not copy to clipboard');
    });
  };

  const toggleErrorSelect = (errorId) => {
    setSelectedErrorIds(prev => {
      const next = new Set(prev);
      if (next.has(errorId)) next.delete(errorId);
      else next.add(errorId);
      return next;
    });
  };

  const unresolvedErrors = errors.filter(e => e.status !== 'resolved');
  const allUnresolvedSelected = unresolvedErrors.length > 0 && unresolvedErrors.every(e => selectedErrorIds.has(e.error_id));

  const toggleSelectAll = () => {
    if (allUnresolvedSelected) {
      setSelectedErrorIds(new Set());
    } else {
      setSelectedErrorIds(new Set(unresolvedErrors.map(e => e.error_id)));
    }
  };

  const buildSingleBlock = (err) => {
    const lines = [
      `### Error: ${err.error_id}`,
      `- **Message**: ${err.error_message}`,
      `- **Source**: ${err.source} | **Type**: ${err.error_type}`,
      err.status_code ? `- **HTTP**: ${err.status_code}` : null,
      err.endpoint ? `- **Endpoint**: ${err.method || '?'} ${err.endpoint}` : null,
      err.cause ? `- **Cause**: ${err.cause}` : null,
      err.user_email ? `- **User**: ${err.user_email}` : null,
      err.page_url ? `- **Page**: ${err.page_url}` : null,
      `- **When**: ${err.created_at}`,
    ].filter(Boolean);
    if (err.stack_trace) lines.push('```', err.stack_trace, '```');
    if (err.request_body) lines.push('```json', JSON.stringify(err.request_body, null, 2), '```');
    return lines.join('\n');
  };

  const copyBulkFixPrompt = async () => {
    const selected = errors.filter(e => selectedErrorIds.has(e.error_id));
    if (!selected.length) return;

    // Fetch full details for each selected error
    const detailed = await Promise.all(
      selected.map(async (e) => {
        try {
          const res = await axios.get(`${API}/errors/${e.error_id}`, { headers });
          return res.data;
        } catch { return e; }
      })
    );

    const ids = detailed.map(e => e.error_id);
    const blocks = detailed.map(buildSingleBlock).join('\n\n---\n\n');

    const prompt = [
      `Fix the following ${detailed.length} errors from the error_logs database. For each one: investigate, fix, test, and mark resolved.`,
      ``,
      `## Errors to Fix`,
      ``,
      blocks,
      ``,
      `## Instructions`,
      `1. Read all ${detailed.length} errors from the error_logs collection`,
      `2. Group related errors if they share the same root cause`,
      `3. Fix each root cause in the codebase`,
      `4. Test all fixes`,
      `5. Mark each error as resolved via PATCH /api/errors/{error_id}/resolve with resolution notes`,
      ``,
      `Error IDs: ${ids.join(', ')}`,
    ].join('\n');

    navigator.clipboard.writeText(prompt).then(() => {
      toast.success(`${detailed.length} errors copied — paste in Emergent chat to batch-fix`, { duration: 5000 });
    }).catch(() => toast.error('Could not copy to clipboard'));
  };

  useEffect(() => { fetchStats().finally(() => setLoading(false)); }, [fetchStats]);
  useEffect(() => {
    // Always fetch error counts for the stat card
    if (token && !denied) {
      axios.get(`${API}/errors`, { headers, params: { limit: 1 } })
        .then(res => setErrorsCounts(res.data.counts))
        .catch(() => {});
    }
  }, [token, denied]);
  useEffect(() => { if (tab === 'users') fetchUsers(); }, [tab, fetchUsers]);
  useEffect(() => { if (tab === 'qrcodes') fetchQrCodes(); }, [tab, fetchQrCodes]);
  useEffect(() => { if (tab === 'errors') fetchErrors(); }, [tab, fetchErrors]);

  const toggleAdmin = async (userId, isAdmin) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/admin`, { is_admin: !isAdmin }, { headers });
      toast.success(isAdmin ? 'Admin revoked' : 'Admin granted');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <SkipLink /><Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8]" />
        </main>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <SkipLink /><Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <Card className="p-12 text-center max-w-md" data-testid="admin-denied">
            <Shield className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Access Denied</h2>
            <p className="text-[hsl(var(--muted-foreground))]">You need admin privileges to access this panel.</p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="admin-panel">
      <SkipLink /><Header />
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-black mb-6" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-title">
            Admin Panel
          </h1>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
              <StatCard icon={Users} label="Users" value={stats.users} color="bg-blue-50 text-blue-600" />
              <StatCard icon={QrCode} label="QR Codes" value={stats.qr_codes} color="bg-emerald-50 text-emerald-600" />
              <StatCard icon={Activity} label="Sessions" value={stats.total_sessions} color="bg-violet-50 text-violet-600" />
              <StatCard icon={Activity} label="Completed" value={stats.completed_sessions} color="bg-amber-50 text-amber-600" />
              <StatCard icon={Clock} label="Audit Entries" value={stats.audit_entries} color="bg-cyan-50 text-cyan-600" />
              <StatCard icon={Users} label="Team Members" value={stats.team_members} color="bg-rose-50 text-rose-600" />
              <StatCard icon={AlertTriangle} label="Open Errors" value={errorsCounts.open} color="bg-red-50 text-red-600" />
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-[hsl(var(--border))]">
            {[
              { key: 'users', label: 'Users' },
              { key: 'qrcodes', label: 'QR Codes' },
              { key: 'errors', label: `Error Logs${errorsCounts.open > 0 ? ` (${errorsCounts.open})` : ''}` },
            ].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setSearch(''); }}
                className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                  tab === t.key ? 'border-b-2 border-[#1d4ed8] text-[#1d4ed8]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                }`} data-testid={`tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setUsersPage(1); setQrPage(1); }}
              placeholder={tab === 'users' ? 'Search users...' : 'Search QR codes...'}
              className="pl-9" data-testid="admin-search" />
          </div>

          {/* Users Tab */}
          {tab === 'users' && (
            <div data-testid="admin-users">
              <div className="space-y-2">
                {users.map(u => (
                  <Card key={u.id} className="p-4 border border-[hsl(var(--border))] flex items-center justify-between" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-[#1d4ed8] flex items-center justify-center shrink-0">
                        <span className="text-white font-bold text-sm">{(u.name || u.email).charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{u.name || 'No name'}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{u.email}</p>
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))] ml-2">{u.qr_count} QRs</span>
                      {u.is_admin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">Admin</span>
                      )}
                      {u.auth_provider === 'google' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">Google</span>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => toggleAdmin(u.id, u.is_admin)}
                      className="rounded-full" data-testid={`toggle-admin-${u.id}`}>
                      {u.is_admin ? <ShieldOff className="h-4 w-4 text-amber-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
                    </Button>
                  </Card>
                ))}
              </div>
              {usersTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)} data-testid="users-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Page {usersPage}/{usersTotalPages} ({usersTotal} users)</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={usersPage >= usersTotalPages} onClick={() => setUsersPage(p => p + 1)} data-testid="users-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* QR Codes Tab */}
          {tab === 'qrcodes' && (
            <div data-testid="admin-qrcodes">
              <div className="space-y-2">
                {qrCodes.map(qr => (
                  <Card key={qr.id} className="p-4 border border-[hsl(var(--border))]" data-testid={`qr-row-${qr.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{qr.title}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{qr.form_url}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
                        <span>{qr.views_count || 0} views</span>
                        <span>{qr.submissions_count || 0} subs</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {qrTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={qrPage <= 1} onClick={() => setQrPage(p => p - 1)} data-testid="qr-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Page {qrPage}/{qrTotalPages} ({qrTotal} QRs)</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={qrPage >= qrTotalPages} onClick={() => setQrPage(p => p + 1)} data-testid="qr-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Error Logs Tab */}
          {tab === 'errors' && (
            <div data-testid="admin-errors">
              {/* Select All + Filters */}
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 cursor-pointer shrink-0" data-testid="select-all-errors">
                  <input
                    type="checkbox"
                    checked={allUnresolvedSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-[hsl(var(--border))] accent-[#1d4ed8]"
                  />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">All</span>
                </label>
                <div className="flex flex-wrap gap-2">
                {[
                  { val: '', label: 'All' },
                  { val: 'open', label: 'Open' },
                  { val: 'investigating', label: 'Investigating' },
                  { val: 'resolved', label: 'Resolved' },
                ].map(f => (
                  <button
                    key={f.val}
                    onClick={() => { setErrorsFilter(f.val); setErrorsPage(1); setSelectedErrorIds(new Set()); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      errorsFilter === f.val
                        ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                        : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[#1d4ed8]'
                    }`}
                    data-testid={`error-filter-${f.val || 'all'}`}
                  >
                    {f.label}
                    {f.val === 'open' && errorsCounts.open > 0 && ` (${errorsCounts.open})`}
                    {f.val === 'investigating' && errorsCounts.investigating > 0 && ` (${errorsCounts.investigating})`}
                    {f.val === 'resolved' && errorsCounts.resolved > 0 && ` (${errorsCounts.resolved})`}
                  </button>
                ))}
                <div className="border-l border-[hsl(var(--border))] mx-1" />
                {[
                  { val: '', label: 'All Sources' },
                  { val: 'backend', label: 'Backend' },
                  { val: 'frontend', label: 'Frontend' },
                ].map(f => (
                  <button
                    key={f.val}
                    onClick={() => { setErrorsSourceFilter(f.val); setErrorsPage(1); setSelectedErrorIds(new Set()); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      errorsSourceFilter === f.val
                        ? 'bg-[#1d4ed8] text-white border-[#1d4ed8]'
                        : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[#1d4ed8]'
                    }`}
                    data-testid={`error-source-${f.val || 'all'}`}
                  >
                    {f.label}
                  </button>
                ))}
                </div>
              </div>

              {/* Error List */}
              <div className="space-y-2">
                {errors.length === 0 && (
                  <Card className="p-8 text-center border border-[hsl(var(--border))]">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">No errors found</p>
                  </Card>
                )}
                {errors.map(err => (
                  <Card
                    key={err.error_id}
                    className={`p-4 border cursor-pointer hover:border-[#1d4ed8] transition-colors ${
                      selectedErrorIds.has(err.error_id) ? 'ring-2 ring-[#1d4ed8]/30 ' : ''
                    }${
                      err.status === 'resolved' ? 'border-emerald-200 bg-emerald-50/30' :
                      err.status === 'investigating' ? 'border-amber-200 bg-amber-50/30' :
                      'border-red-200 bg-red-50/30'
                    }`}
                    onClick={() => openErrorDetail(err.error_id)}
                    data-testid={`error-row-${err.error_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {err.status !== 'resolved' && (
                          <input
                            type="checkbox"
                            checked={selectedErrorIds.has(err.error_id)}
                            onChange={(e) => { e.stopPropagation(); toggleErrorSelect(err.error_id); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 mt-1 rounded border-[hsl(var(--border))] accent-[#1d4ed8] shrink-0 cursor-pointer"
                            data-testid={`select-error-${err.error_id}`}
                          />
                        )}
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          err.status === 'resolved' ? 'bg-emerald-100 text-emerald-600' :
                          err.status === 'investigating' ? 'bg-amber-100 text-amber-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {err.status === 'resolved' ? <CheckCircle2 size={16} /> :
                           err.status === 'investigating' ? <Eye size={16} /> :
                           <Bug size={16} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate">{err.error_message}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] font-mono">
                              {err.error_id.slice(0, 8)}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              err.source === 'backend' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'
                            }`}>
                              {err.source}
                            </span>
                            {err.endpoint && (
                              <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono truncate max-w-[200px]">
                                {err.method} {err.endpoint}
                              </span>
                            )}
                            {err.user_email && (
                              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                                {err.user_email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0 flex flex-col items-end gap-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          err.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                          err.status === 'investigating' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {err.status}
                        </span>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
                          {new Date(err.created_at).toLocaleString()}
                        </p>
                        {err.status !== 'resolved' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openErrorDetail(err.error_id).then(() => {}); copyFixPrompt(err); }}
                            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#1d4ed8] text-white hover:bg-[#1e40af] transition-colors mt-0.5"
                            data-testid={`fix-btn-${err.error_id}`}
                          >
                            <Wand2 size={10} /> Fix
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {errorsTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={errorsPage <= 1} onClick={() => setErrorsPage(p => p - 1)} data-testid="errors-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Page {errorsPage}/{errorsTotalPages} ({errorsTotal} errors)</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={errorsPage >= errorsTotalPages} onClick={() => setErrorsPage(p => p + 1)} data-testid="errors-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Bulk Selection Floating Bar */}
          {selectedErrorIds.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0F172A] text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4" data-testid="bulk-action-bar">
              <span className="text-sm font-medium">{selectedErrorIds.size} error{selectedErrorIds.size > 1 ? 's' : ''} selected</span>
              <Button
                size="sm"
                className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white"
                onClick={copyBulkFixPrompt}
                data-testid="bulk-fix-btn"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy Bulk Fix Prompt
              </Button>
              <button
                onClick={() => setSelectedErrorIds(new Set())}
                className="text-xs text-[#94A3B8] hover:text-white transition-colors"
                data-testid="bulk-clear-btn"
              >
                Clear
              </button>
            </div>
          )}

          {/* Error Detail Modal */}
          {selectedError && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelectedError(null)} data-testid="error-detail-modal">
              <div className="bg-[hsl(var(--background))] rounded-2xl border border-[hsl(var(--border))] shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        selectedError.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                        selectedError.status === 'investigating' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {selectedError.status}
                      </span>
                      <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">{selectedError.error_id}</span>
                    </div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{selectedError.error_message}</h3>
                  </div>
                  <button onClick={() => setSelectedError(null)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] text-lg">✕</button>
                </div>

                {/* Meta Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Source</p>
                    <p className="text-sm font-medium">{selectedError.source}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Type</p>
                    <p className="text-sm font-medium font-mono">{selectedError.error_type}</p>
                  </div>
                  {selectedError.status_code && (
                    <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                      <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Status Code</p>
                      <p className="text-sm font-medium">{selectedError.status_code}</p>
                    </div>
                  )}
                  {selectedError.endpoint && (
                    <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                      <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Endpoint</p>
                      <p className="text-sm font-medium font-mono truncate">{selectedError.method} {selectedError.endpoint}</p>
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">User</p>
                    <p className="text-sm font-medium">{selectedError.user_email || selectedError.user_id || 'Anonymous'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">When</p>
                    <p className="text-sm font-medium">{new Date(selectedError.created_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* Cause */}
                {selectedError.cause && (
                  <div className="mb-4">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Cause</p>
                    <p className="text-sm bg-[hsl(var(--muted))] p-3 rounded-lg">{selectedError.cause}</p>
                  </div>
                )}

                {/* Page URL */}
                {selectedError.page_url && (
                  <div className="mb-4">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Page URL</p>
                    <p className="text-xs font-mono bg-[hsl(var(--muted))] p-3 rounded-lg break-all">{selectedError.page_url}</p>
                  </div>
                )}

                {/* Stack Trace */}
                {selectedError.stack_trace && (
                  <div className="mb-4">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Stack Trace</p>
                    <pre className="text-xs bg-[#0F172A] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto max-h-48 whitespace-pre-wrap">{selectedError.stack_trace}</pre>
                  </div>
                )}

                {/* Request Body */}
                {selectedError.request_body && (
                  <div className="mb-4">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Request Body</p>
                    <pre className="text-xs bg-[#0F172A] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto max-h-32 whitespace-pre-wrap">{JSON.stringify(selectedError.request_body, null, 2)}</pre>
                  </div>
                )}

                {/* Browser Info */}
                {selectedError.browser_info && (
                  <div className="mb-4">
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1">Browser</p>
                    <p className="text-xs font-mono bg-[hsl(var(--muted))] p-3 rounded-lg break-all">{selectedError.browser_info}</p>
                  </div>
                )}

                {/* Resolution Notes (if resolved) */}
                {selectedError.status === 'resolved' && selectedError.resolution_notes && (
                  <div className="mb-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-[10px] text-emerald-600 uppercase tracking-wider mb-1">Resolution</p>
                    <p className="text-sm text-emerald-800">{selectedError.resolution_notes}</p>
                    <p className="text-[10px] text-emerald-500 mt-2">Resolved: {new Date(selectedError.resolved_at).toLocaleString()}</p>
                  </div>
                )}

                {/* Actions */}
                {selectedError.status !== 'resolved' && (
                  <div className="border-t border-[hsl(var(--border))] pt-4 mt-4 space-y-3">
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                        onClick={() => copyFixPrompt(selectedError)}
                        data-testid="btn-fix-emergent"
                      >
                        <Wand2 className="h-4 w-4 mr-1" /> Fix with Emergent
                      </Button>
                      {selectedError.status === 'open' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-amber-300 text-amber-600 hover:bg-amber-50"
                          onClick={() => investigateError(selectedError.error_id)}
                          data-testid="btn-investigate"
                        >
                          <Eye className="h-4 w-4 mr-1" /> Investigate
                        </Button>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-[hsl(var(--muted-foreground))] block mb-1">Resolution Notes (required to resolve)</label>
                      <textarea
                        className="w-full border border-[hsl(var(--border))] rounded-lg p-3 text-sm bg-[hsl(var(--background))] resize-none"
                        rows={3}
                        placeholder="Describe what fixed this error..."
                        value={resolveNotes}
                        onChange={e => setResolveNotes(e.target.value)}
                        data-testid="resolve-notes-input"
                      />
                      <Button
                        size="sm"
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 mt-2"
                        onClick={() => resolveError(selectedError.error_id)}
                        disabled={!resolveNotes.trim()}
                        data-testid="btn-resolve"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve & Archive
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
